import { type SyncFailure, type SyncStatus, syncStatuses, type TilPartStatus } from "@nexxt/contracts/til";
import { NativeSelect, StatusBadge } from "@nexxt/ui";
import { syncStatusTone } from "@nexxt/ui/status";
import { useEffect, useRef } from "react";
import { m } from "../paraglide/messages.js";

// How an entity stands towards Business Central, in the words of the app: the same for a linesheet, a purchase order
// and the customer or the supplier of an organization, as the domains use one vocabulary for it (ADR-106). An
// association of a contact uses the same vocabulary towards HubSpot, with its own words (ADR-116).

/** How often a screen that waits for an outcome asks again: the outcomes arrive on their own, minutes later. */
export const OUTCOME_POLL_MS = 10_000;

export const syncStatusLabel: Record<SyncStatus, () => string> = {
  not_synced: m.sync_status_not_synced,
  sync_in_progress: m.sync_status_in_progress,
  sync_failed: m.sync_status_failed,
  sync_complete: m.sync_status_complete,
};

/** The same vocabulary towards the warehouse (I-08, I-09): what it has is "at the warehouse". */
export const warehouseSyncLabel: Record<SyncStatus, () => string> = {
  ...syncStatusLabel,
  sync_complete: m.sync_status_at_warehouse,
};

/** The entries a sync leaves in the history of what it sends: the same actions and fields for every entity. */
export const syncHistoryActions: Record<string, () => string> = {
  sync_in_progress: m.history_sync_in_progress,
  sync_complete: m.history_sync_complete,
  sync_failed: m.history_sync_failed,
  // a read of the body of a message with TIL, from Backstage (ADR-164)
  payload_read: m.history_payload_read,
};
export const syncHistoryFields: Record<string, () => string> = {
  bcSyncStatus: m.col_bc_sync,
  bcSyncRevision: m.sync_field_revision,
};

/**
 * How an entity stands towards a system, as a badge: Business Central unless `labels` gives the words of another
 * (HubSpot, for an association: ADR-116). The tone is the same for every system.
 */
export function SyncStatusBadge({
  status,
  labels = syncStatusLabel,
}: {
  status: SyncStatus | undefined;
  labels?: Record<SyncStatus, () => string>;
}) {
  return status ? <StatusBadge tone={syncStatusTone[status]} label={labels[status]()} /> : null;
}

/** The filter of a list by how its items stand on Business Central. */
export function BcSyncFilter({
  value,
  onChange,
}: {
  value: SyncStatus | undefined;
  onChange: (next: SyncStatus | undefined) => void;
}) {
  return (
    <NativeSelect
      aria-label={m.col_bc_sync()}
      value={value ?? ""}
      onChange={(e) => onChange(syncStatuses.find((status) => status === e.target.value))}
    >
      <option value="">{m.filter_any_bc_sync()}</option>
      {syncStatuses.map((status) => (
        <option key={status} value={status}>
          {syncStatusLabel[status]()}
        </option>
      ))}
    </NativeSelect>
  );
}

/**
 * Calls `onChange` when what a screen follows while it waits for an outcome changes after it was first read: the
 * outcome arrived on its own, and what else shows the entity is to be read again. `standing` is undefined until read.
 */
export function useOutcomeChange(standing: string | undefined, onChange: () => void) {
  const seen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (standing === undefined) return;
    if (seen.current !== undefined && seen.current !== standing) onChange();
    seen.current = standing;
  }, [standing, onChange]);
}

/** Why the last state sent is not there, and what to do about it. */
export const syncFailureText: Record<SyncFailure, () => string> = {
  not_ready: m.sync_failure_not_ready,
  message_dead: m.sync_run_error_message_dead,
  failed: m.sync_failure_failed,
  rejected: m.sync_failure_rejected,
};

/** The same towards the warehouse (I-08, I-09, I-10): what it lacks or refused is the warehouse's, not Business Central's. */
export const warehouseSyncFailureText: Record<SyncFailure, () => string> = {
  ...syncFailureText,
  not_ready: m.sync_failure_warehouse_not_ready,
  failed: m.sync_failure_warehouse_failed,
  rejected: m.sync_failure_warehouse_rejected,
};

/** How a message of the last state sent stands. */
export const syncMessageLabel: Record<TilPartStatus, () => string> = {
  sent: m.sync_message_sent,
  processed: m.sync_message_processed,
  rejected: m.sync_message_rejected,
  failed: m.sync_message_failed,
  dead: m.sync_message_dead,
};
